import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { User, Lock, Mail, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Alert } from '../design-system';
import analyticsService from '../../services/analyticsService';

const LoginModal = ({ onClose, onModalOpen }) => {
  const { user, login, signup, error } = useAuth();
  
  // Track modal open
  useEffect(() => {
    if (onModalOpen) {
      // Determine trigger from URL or default
      const trigger = 'button_click'; // Default, can be enhanced
      onModalOpen(trigger);
    }
  }, [onModalOpen]);

  useEffect(() => {
    // If user is already logged in, close modal
    if (user) {
      onClose();
    }
  }, [user, onClose]);

  const handleLogin = () => {
    login();
  };

  const handleSignup = () => {
    try {
      analyticsService.track('signup_started', {
        source: 'landing_page',
        timeOnPage: null // Will be calculated from landing page if needed
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
    signup();
  };

  if (user) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div style={{ textAlign: 'center' }}>
        <div 
          style={{ 
            margin: '0 auto',
            height: '48px',
            width: '48px',
            backgroundColor: 'var(--color-primary)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <User style={{ height: '24px', width: '24px', color: 'white' }} />
        </div>
        <h2 style={{ 
          marginTop: 'var(--space-4)', 
          fontSize: 'var(--text-h2)', 
          fontWeight: 'var(--weight-bold)', 
          color: 'var(--color-text)',
          fontFamily: 'var(--font-heading)',
          marginBottom: 'var(--space-2)'
        }}>
          Welcome to TheraEngage
        </h2>
        <p style={{ 
          fontSize: 'var(--text-caption)', 
          color: 'var(--color-text)', 
          opacity: 0.8,
          margin: 0
        }}>
          Manage your patients and appointments
        </p>
      </div>

      {error && (
        <Alert variant="error" size="medium">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <AlertCircle style={{ height: '16px', width: '16px', marginRight: 'var(--space-2)' }} />
            <p style={{ margin: 0, fontSize: 'var(--text-caption)' }}>{error}</p>
          </div>
        </Alert>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ 
            fontSize: 'var(--text-body)', 
            fontWeight: 'var(--weight-semibold)', 
            color: 'var(--color-text)',
            margin: 0
          }}>
            Sign in to your account
          </h3>
        </div>

        <Button
          variant="primary"
          size="large"
          onClick={handleLogin}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}
        >
          <Mail style={{ height: '20px', width: '20px' }} />
          Sign In
        </Button>

        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <span style={{ 
              padding: '0 var(--space-2)', 
              backgroundColor: 'white', 
              color: 'var(--color-text)', 
              opacity: 0.6,
              fontSize: 'var(--text-caption)'
            }}>
              New to TheraEngage?
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          size="large"
          onClick={handleSignup}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}
        >
          <Lock style={{ height: '20px', width: '20px' }} />
          Create Account
        </Button>
      </div>

      <div style={{ textAlign: 'center' }}>
        <p style={{ 
          fontSize: '12px', 
          color: 'var(--color-text)', 
          opacity: '0.6',
          margin: 0
        }}>
          Secure authentication powered by Auth0
        </p>
      </div>
    </div>
  );
};

export default LoginModal; 